import { Button, Card, Divider, Image, Modal, Spin, Tag } from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useSafeBackNavigation } from "../../../../shared/Route/useSafeBackNavigation";
import { useApp } from "../../../../shared/AppProvider/AppProvider";
import { sleep } from "../../../../utils";
import { useAppDispatch } from "../../../../store/store";
import { DeletePetThunk, GetPetByIdThunk } from "../../redux/thunk";
import { SkeletonViewPetProfile } from "./Skeletons/SkeletonViewPetProfile";
import useProfile from "../../hooks/useProfile";
import { useParams } from "react-router-dom";

const InfoItem = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => {
  return (
    <div className="space-y-1">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="text-gray-900 dark:text-white font-medium">
        {value || "-"}
      </div>
    </div>
  );
};

export const ViewPetProfile = () => {
  const { navigate } = useSafeBackNavigation("/");
  const [selectedImage, setSelectedImage] = useState<string | undefined>();
  const { pet } = useProfile();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { notify } = useApp();
  const { pet_id } = useParams<{ pet_id: string }>();

  useEffect(() => {
    if (pet) return;

    const handleViewPet = async (pet_id: string) => {
      if (!pet_id) return;

      try {
        await sleep(300);
        await dispatch(GetPetByIdThunk({ pet_id })).unwrap();
        navigate(`/profile/my-pets/view/${pet_id}`);
      } catch (error: any) {
        notify("error", error?.message || "Something went wrong");
      }
    };

    handleViewPet(pet_id as string);
  }, []);

  const primaryImage = useMemo(() => {
    return pet?.images.find((img) => img.is_primary) || pet?.images?.[0];
  }, [pet?.images]);

  const activeImage = selectedImage || primaryImage?.url;

  const handleEditPet = () => {
    navigate(`/profile/my-pets/edit/${pet?.id}`);
  };

  const handleOpenDeleteModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    if (deleteLoading) return;

    setOpen(false);
  };

  const handleOk = async () => {
    try {
      setOpen(false);
      setDeleteLoading(true);

      await sleep(300);

      await dispatch(
        DeletePetThunk({
          pet_id: pet?.id as string,
        }),
      ).unwrap();

      notify("success", "Pet deleted successfully");

      setOpen(false);

      navigate("/profile/my-pets");
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error(error.message);
        notify("error", error?.message || "Something went wrong");
      } else {
        notify("error", "Something went wrong");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!pet) {
    return <SkeletonViewPetProfile />;
  }

  return (
    <Spin
      spinning={deleteLoading}
      indicator={<LoadingOutlined spin />}
      size="large"
    >
      <div className="flex flex-col gap-6">
        {/* HEADER */}
        <div className="flex gap-4 items-start">
          <Button
            className="dark:!text-white dark:!bg-transparent dark:hover:!bg-gray-600"
            type="default"
            icon={<ArrowLeftOutlined style={{ fontSize: 16 }} />}
            onClick={() => navigate("/profile/my-pets")}
          />

          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {pet.name}
            </h2>

            <p className="text-gray-500 dark:text-gray-400">
              {pet.species} • {pet.breed || "Unknown Breed"}
            </p>
          </div>
        </div>

        {/* IMAGE GALLERY */}
        <div className="space-y-3">
          <div className="relative w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5">
            {/* ACTION BUTTONS */}

            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <Button
                icon={<EditOutlined />}
                onClick={handleEditPet}
                className="!bg-white/90 backdrop-blur border-none shadow dark:!bg-gray-800/90 dark:!text-white"
              >
                Edit
              </Button>

              <Button
                danger
                loading={deleteLoading}
                icon={<DeleteOutlined />}
                className="shadow"
                onClick={handleOpenDeleteModal}
              >
                Delete
              </Button>
            </div>
            {activeImage ? (
              <Image
                src={activeImage}
                preview={false}
                className="!w-full !h-full"
                style={{
                  objectFit: "cover",
                }}
                id="pet-image"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                No Image
              </div>
            )}
          </div>

          {/* THUMBNAILS */}
          <div className="flex gap-3 flex-wrap">
            {pet.images.map((image) => {
              const isActive = activeImage === image.url;

              return (
                <div
                  key={image.key}
                  onClick={() => setSelectedImage(image.url)}
                  className={`
                  relative w-24 h-24 rounded-xl overflow-hidden cursor-pointer transition shadow border-2 
                  ${isActive ? "border-blue-500" : "border-gray-50"}
                `}
                >
                  <Image
                    src={image.url}
                    preview={false}
                    className="!w-24 !h-24"
                    style={{
                      objectFit: "cover",
                    }}
                  />

                  {image.is_primary && (
                    <div className="absolute bottom-1 left-1 bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full">
                      Primary
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* BASIC INFO */}
        <Card
          title={<span className="dark:text-white">Basic Information</span>}
          className="h-full !bg-white dark:!bg-gray-700"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoItem label="Pet Name" value={pet.name} />
            <InfoItem label="Species" value={pet.species} />
            <InfoItem label="Breed" value={pet.breed} />
            <InfoItem
              label="Gender"
              value={pet.gender === "M" ? "Male" : "Female"}
            />
            <InfoItem label="Age" value={`${pet.age} years`} />
            <InfoItem
              label="Size"
              value={<Tag className="capitalize">{pet.size}</Tag>}
            />
          </div>
        </Card>

        {/* HEALTH */}
        <Card
          title={<span className="dark:text-white">Health</span>}
          className="h-full !bg-white dark:!bg-gray-700"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoItem
              label="Vaccinated"
              value={pet.vaccinated ? "Yes" : "No"}
            />

            <InfoItem
              label="Medical Conditions"
              value={pet.medical_conditions}
            />

            <InfoItem label="Allergies" value={pet.allergies} />

            <InfoItem label="Medications" value={pet.medications} />

            <InfoItem label="Vet Contact" value={pet.vetContact} />
          </div>
        </Card>

        {/* ROUTINE */}
        <Card
          title={<span className="dark:text-white">Routine</span>}
          className="h-full !bg-white dark:!bg-gray-700"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoItem label="Feeding Schedule" value={pet.feeding_schedule} />

            <InfoItem label="Food Notes" value={pet.food_notes} />

            <InfoItem label="Walks Per Day" value={pet.walks_per_day} />

            <InfoItem
              label="Walk Duration"
              value={`${pet.walk_duration} mins`}
            />
          </div>
        </Card>

        {/* BEHAVIOR */}
        <Card
          title={<span className="dark:text-white">Behavior</span>}
          className="h-full !bg-white dark:!bg-gray-700"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoItem
              label="Friendly with Dogs"
              value={pet.friendly_dogs ? "Yes" : "No"}
            />

            <InfoItem
              label="Friendly with Cats"
              value={pet.friendly_cats ? "Yes" : "No"}
            />

            <InfoItem
              label="Friendly with Children"
              value={pet.friendly_children ? "Yes" : "No"}
            />

            <InfoItem
              label="Energy Level"
              value={<Tag className="capitalize">{pet.energy_level}</Tag>}
            />
          </div>

          {pet.behavior_notes && (
            <>
              <Divider />

              <InfoItem label="Special Notes" value={pet.behavior_notes} />
            </>
          )}
        </Card>
      </div>

      <Modal
        title="Delete pet profile"
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete {pet.name}'s profile?</p>
      </Modal>
    </Spin>
  );
};
